import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "474",
  "slug": "what-is-an-earthquake-how-the-ground-shakes",
  "title": "What Is an Earthquake? How and Why the Ground Shakes",
  "excerpt": "Earthquakes release energy that has been building for years along faults deep underground. Here is what actually causes the shaking, how we measure it, and why most quakes strike the same few places.",
  "date": "2026-06-18",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1563863709-3f25fdebed75?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxlYXJ0aHF1YWtlJTIwY3JhY2tlZCUyMGdyb3VuZHxlbnwxfDB8fHwxNzgxNzcwNDIzfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "An earthquake is the shaking caused when stress stored in rock along a fault is suddenly released, sending seismic waves through the ground. Most happen at the boundaries of tectonic plates, which is why roughly 90 percent of them strike around the Pacific Ring of Fire. Magnitude measures the energy released; intensity measures how strongly the shaking is felt at the surface.",
  "content": [
    {
      "type": "paragraph",
      "text": "The ground beneath your feet feels permanent, but it is under constant strain. The outer shell of the Earth is broken into enormous slabs of rock, the tectonic plates, and they are always grinding past, pulling away from, or colliding with one another. Where they meet, rock gets squeezed and stretched. An earthquake is what happens when that stored stress is finally released all at once."
    },
    {
      "type": "heading",
      "text": "Faults and the Elastic Rebound"
    },
    {
      "type": "paragraph",
      "text": "Most earthquakes happen along faults, fractures in the crust where two blocks of rock can move relative to each other. Friction usually locks a fault in place, so instead of sliding smoothly, the rock on either side bends and deforms as the plates push on it, storing energy like a compressed spring. When the stress finally exceeds what the friction can hold, the fault slips suddenly and the rock snaps back toward its unstrained shape. That snap, called elastic rebound, releases the stored energy as the seismic waves we feel as shaking."
    },
    {
      "type": "paragraph",
      "text": "The point underground where the rupture begins is the focus, or hypocentre. The spot on the surface directly above it is the epicentre, which is why news reports describe quakes as striking a certain distance from a city."
    },
    {
      "type": "heading",
      "text": "Seismic Waves: How the Shaking Travels"
    },
    {
      "type": "paragraph",
      "text": "The energy radiates outward in several kinds of waves, and they arrive in a predictable order:"
    },
    {
      "type": "list",
      "items": [
        "P-waves, or primary waves, travel fastest and arrive first. They push and pull the ground in the direction they are moving, like sound through rock.",
        "S-waves, or secondary waves, are slower and shake the ground side to side. They cannot pass through liquid, which is one way scientists deduced that Earth has a liquid outer core.",
        "Surface waves arrive last and travel along the ground itself. They are usually the most destructive, rolling and rippling the surface where buildings stand."
      ]
    },
    {
      "type": "paragraph",
      "text": "Because P-waves outrun the more damaging waves, early-warning systems can sometimes detect the first tremor and send an alert seconds before the strong shaking arrives. A few seconds does not sound like much, but it is enough to stop trains, halt surgeries, and let people take cover."
    },
    {
      "type": "heading",
      "text": "Magnitude vs Intensity"
    },
    {
      "type": "paragraph",
      "text": "Two different numbers describe an earthquake, and they are often confused. Magnitude measures the energy released at the source, and modern science uses the moment magnitude scale, which has largely replaced the older Richter scale for larger quakes. The scale is logarithmic: each whole number up represents about 32 times more energy, so a magnitude 7 releases roughly a thousand times the energy of a magnitude 5."
    },
    {
      "type": "paragraph",
      "text": "Intensity, by contrast, measures how strongly the shaking is actually felt and how much damage it does at a given place, using scales like the Modified Mercalli scale. The same earthquake has one magnitude but many intensities, strong near the epicentre and gentle far away, depending on distance, depth, and the type of ground."
    },
    {
      "type": "heading",
      "text": "Why Quakes Cluster in the Same Places"
    },
    {
      "type": "paragraph",
      "text": "Earthquakes are not scattered randomly. They trace the edges of the tectonic plates almost exactly. The Pacific Ring of Fire, the horseshoe of plate boundaries circling the Pacific Ocean, accounts for around 90 percent of the world's earthquakes. Other major belts run through the Mediterranean and across the Himalaya, where the Indian plate is still ramming into Asia. Undersea quakes can displace huge volumes of water and trigger tsunamis, which is why coastal warning systems watch seismographs so closely."
    },
    {
      "type": "heading",
      "text": "Foreshocks, Aftershocks, and Why Prediction Is So Hard"
    },
    {
      "type": "paragraph",
      "text": "A big earthquake is rarely a single event. Smaller foreshocks sometimes precede the main rupture, and a long sequence of aftershocks follows it as the crust settles into its new arrangement, occasionally for months. The frustrating reality is that scientists still cannot predict exactly when and where a specific earthquake will strike. What they can do is calculate long-term probabilities for a region and identify which faults are overdue, which is why earthquake-prone places focus less on forecasting the day and more on building to survive the shaking. Sound construction, not prediction, is what saves lives."
    }
  ]
};
